import { Injectable } from '@angular/core';
import {
    CreditMethodsType,
    PaymentCart,
    PaymentMethods,
    PaymentMethodsType,
    PaymentToken,
} from '@cargos/sprintpay-models';
import { BehaviorSubject, distinctUntilChanged, map, mergeMap, Observable, of, switchMap, take } from 'rxjs';
import { PaymentMethodsService, SessionService } from 'src/app/shared/services';
import { paymentMethodGuestInitialValues } from 'src/app/shared/services/summary/models/types';
import {
    PaymentMethodComplete,
    PaymentMethodGuest,
    PaymentMethodSelected,
    PaymentMethodsSelected,
} from '../models/payment-method.model';

import { Card, Echeck, PaymentMethod, PaymentMethodsEvents } from '@cargos/sprintpay-models';
export type CartGroupByFlight = PaymentCart[][];
interface EventPaymentMethod {
    item: Card | Echeck | undefined;
    method: PaymentMethodsType | null;
}

@Injectable({
    providedIn: 'root',
})
export class PaymentMethodsHandlerService {
    private paymentMethodSelected = new BehaviorSubject<PaymentMethodSelected | null>(null);
    private paymentMethodGuest = new BehaviorSubject<PaymentMethodGuest | null>(null);
    private paymentMethodGuestForCC = new BehaviorSubject<PaymentMethodGuest | null>(null);
    private paymentMethodGuestForEcheck = new BehaviorSubject<PaymentMethodGuest | null>(null);
    private paymentMethodSelectedAboveThreshold: BehaviorSubject<PaymentMethodSelected | null> =
        new BehaviorSubject<PaymentMethodSelected | null>(null);

    private guestPaymentInformation: BehaviorSubject<PaymentMethodGuest | null> =
        new BehaviorSubject<PaymentMethodGuest | null>(null);

    get instant_payment_method_selected(): PaymentMethodSelected | null {
        return this.paymentMethodSelected.value;
    }
    private settingDefaultPaymentMethod = new BehaviorSubject<boolean>(false);
    public isAuthenticated: boolean;

    constructor(
        private paymentMethodsService: PaymentMethodsService,
        private sessionService: SessionService
    ) {}

    getPaymentMethodNameSelected$(): Observable<PaymentMethodsType | null> {
        return this.getPaymentMethodSelected$().pipe(
            map((methodSelected: PaymentMethodSelected | null) => methodSelected?.method || null),
            distinctUntilChanged<PaymentMethodsType | null>()
        );
    }

    getPaymentMethodsSelected$(): Observable<{
        paymentMethodSelected: PaymentMethodSelected | null;
        paymentMethodSelectedAboveThreshold: PaymentMethodSelected | null;
    }> {
        return this.getPaymentMethodSelected$(true).pipe(
            switchMap((paymentMethodSelectedAboveThreshold: PaymentMethodSelected | null) => {
                return this.getPaymentMethodSelected$(false).pipe(
                    map((paymentMethodSelected: PaymentMethodSelected | null) => {
                        return {
                            paymentMethodSelected,
                            paymentMethodSelectedAboveThreshold,
                        };
                    })
                );
            })
        );
    }

    getPaymentMethodByUser$(): Observable<PaymentMethodSelected | PaymentMethodGuest | null> {
        return this.sessionService.isAuthenticated$().pipe(
            switchMap((isAuthenticated) => {
                if (isAuthenticated) {
                    return this.getPaymentMethodSelected$();
                }

                return this.getPaymentMethodGuest();
            })
        );
    }

    getPaymentMethodByUser(): PaymentMethodSelected | PaymentMethodGuest | null {
        if (this.sessionService.isAuthenticated()) {
            return this.instant_payment_method_selected;
        }

        return this.instant_guest_payment_method;
    }

    getPaymentMethodNameByUser$(): Observable<PaymentMethodsSelected | null> {
        return this.sessionService.isAuthenticated$().pipe(
            switchMap((isAuthenticated) => {
                if (isAuthenticated) {
                    return this.getPaymentMethodsSelectedName$();
                }
                return this.getPaymentMethodGuest().pipe(
                    map((paymentMethodGuest: PaymentMethodGuest | null) => {
                        if (paymentMethodGuest?.paymentMethod) {
                            return {
                                paymentMethodSelected: paymentMethodGuest?.paymentMethod as PaymentMethodsType,
                                paymentMethodSelectedAboveThreshold: undefined,
                            };
                        }
                        return null;
                    })
                );
            })
        );
    }

    getPaymentMethodSelectedComplete$(aboveThreshold?: boolean): Observable<PaymentMethodComplete | null> {
        return this.getPaymentMethodSelected$(aboveThreshold).pipe(
            switchMap((paymentMethodSelected: PaymentMethodSelected | null) => {
                if (paymentMethodSelected) {
                    const { token } = paymentMethodSelected;

                    return this.paymentMethodsService
                        .getPaymentMethodByTypeAndToken(paymentMethodSelected.method, token)
                        .pipe(
                            map((paymentMethodComplete) => ({
                                paymentMethodComplete,
                                name: paymentMethodSelected.method as PaymentMethodsType,
                                token,
                            }))
                        );
                }

                return of(null);
            })
        );
    }

    setPaymentMethodSelected(values: PaymentMethodSelected | null): void {
        this.paymentMethodSelected.next(
            values
                ? {
                      id: 'authenticatedPaymentMethod',
                      ...values,
                  }
                : values
        );
    }

    get instant_payment_method_guest_for_CC(): PaymentMethodGuest | null {
        return this.paymentMethodGuestForCC.value;
    }

    private setGuestCreditCard(paymentMethodGuest: PaymentMethodGuest): void {
        this.paymentMethodGuestForCC.next(
            paymentMethodGuest
                ? {
                      id: 'guestPaymentMethod',
                      ...paymentMethodGuest,
                  }
                : paymentMethodGuest
        );
    }

    getGuestCreditCard(): Observable<PaymentMethodGuest | null> {
        return this.paymentMethodGuestForCC.asObservable().pipe(distinctUntilChanged());
    }

    resetGuestCreditCard(): void {
        this.paymentMethodGuestForCC.next(paymentMethodGuestInitialValues);
    }

    // TODO: Refactor PaymentMethodGuest to only include the creditCardForm field. Review the definition of <PaymentMethodGuest>.
    setPaymentMethodGuest(paymentMethodGuest: PaymentMethodGuest | null): void {
        if (
            paymentMethodGuest?.paymentMethod === PaymentMethods.CREDIT_CARD &&
            !paymentMethodGuest.creditCardForm &&
            this.instant_payment_method_guest_for_CC?.creditCardForm
        ) {
            this.paymentMethodsService.newEventPaymentMethod(PaymentMethodsEvents.creditCardAdded);
            this.paymentMethodGuest.next(this.instant_payment_method_guest_for_CC);
        } else if (
            paymentMethodGuest?.paymentMethod === PaymentMethods.ECHECK &&
            !paymentMethodGuest.echeckForm &&
            this.instant_payment_method_echeck?.echeckForm
        ) {
            this.paymentMethodsService.newEventPaymentMethod(PaymentMethodsEvents.echeckAdded);
            this.paymentMethodGuest.next(this.instant_payment_method_echeck);
        } else {
            if (
                paymentMethodGuest?.paymentMethod === PaymentMethods.CREDIT_CARD &&
                (paymentMethodGuest?.creditCardNonce || paymentMethodGuest?.paymentVehicle)
            ) {
                this.paymentMethodsService.newEventPaymentMethod(PaymentMethodsEvents.creditCardAdded);
                this.setGuestCreditCard(paymentMethodGuest);
            }

            if (paymentMethodGuest?.paymentMethod === PaymentMethods.ECHECK && paymentMethodGuest.echeckForm) {
                this.paymentMethodsService.newEventPaymentMethod(PaymentMethodsEvents.echeckAdded);
                this.setGuestEcheck(paymentMethodGuest);
            }

            this.paymentMethodGuest.next(
                paymentMethodGuest
                    ? {
                          id: 'guestPaymentMethod',
                          ...paymentMethodGuest,
                      }
                    : paymentMethodGuest
            );
        }
    }

    get instant_guest_payment_method(): PaymentMethodGuest | null {
        return this.paymentMethodGuest.value;
    }

    getPaymentMethodGuest(): Observable<PaymentMethodGuest | null> {
        return this.paymentMethodGuest.asObservable().pipe(distinctUntilChanged());
    }

    removePaymentMethodGuest(): void {
        this.paymentMethodGuest.next(paymentMethodGuestInitialValues);
    }

    isPaymentMethodGuestValid$(): Observable<boolean> {
        return this.getPaymentMethodGuest().pipe(
            map((guestPayment: PaymentMethodGuest | null) => {
                if (guestPayment?.paymentMethod === PaymentMethods.PAYPAL) {
                    return true;
                }

                if (
                    guestPayment?.paymentMethod === PaymentMethods.CREDIT_CARD &&
                    (guestPayment?.creditCardNonce || guestPayment?.paymentVehicle)
                ) {
                    return true;
                }

                if (guestPayment?.paymentMethod === PaymentMethods.ECHECK && guestPayment?.echeckOTToken) {
                    return true;
                }

                return false;
            }),
            distinctUntilChanged()
        );
    }

    isPaymentMethodSelected(): Observable<boolean> {
        return this.getPaymentMethodSelected$().pipe(map((paymentMethodToken) => !!paymentMethodToken));
    }

    get instant_payment_method_echeck(): PaymentMethodGuest | null {
        return this.paymentMethodGuestForEcheck.value;
    }

    private setGuestEcheck(paymentMethodGuest: PaymentMethodGuest): void {
        this.paymentMethodGuestForEcheck.next(
            paymentMethodGuest
                ? {
                      id: 'guestPaymentMethod',
                      ...paymentMethodGuest,
                  }
                : paymentMethodGuest
        );
    }

    getGuestEcheck(): Observable<PaymentMethodGuest | null> {
        return this.paymentMethodGuestForEcheck.asObservable().pipe(distinctUntilChanged());
    }

    resetGuestEcheck(): void {
        this.paymentMethodGuestForEcheck.next(paymentMethodGuestInitialValues);
    }

    /**
     * @method setPaymentMethodSelected
     * @param paymentMethodSelected - The selected payment method to be set when the bill amount is above the threshold.
     * @description Sets the selected payment method when the bill amount is above the threshold.
     */
    setPaymentMethodSelectedAboveThreshold(paymentMethodSelected: PaymentMethodSelected | null): void {
        this.paymentMethodSelectedAboveThreshold.next(paymentMethodSelected);
    }

    /**
     * @method instant_payment_method
     * @description Returns the current selected payment method when the bill amount is above the threshold.
     * @returns The currently selected payment method or null if none is selected when the bill amount is above the threshold.
     */
    get instant_payment_method_above_threshold(): PaymentMethodSelected | null {
        return this.paymentMethodSelectedAboveThreshold.value;
    }

    getPaymentMethodSelected(aboveThreshold?: boolean): PaymentMethodSelected | null {
        if (aboveThreshold) {
            return this.paymentMethodSelectedAboveThreshold.value;
        }
        return this.paymentMethodSelected.value;
    }

    /**
     * @method getPayMethodSelected
     * @description Returns an observable of the selected payment method.
     * @returns An observable of the selected payment method, distinct until changed.
     */
    getPaymentMethodSelected$(aboveThreshold?: boolean): Observable<PaymentMethodSelected | null> {
        return this.sessionService.isAuthenticated$().pipe(
            switchMap((isAuthenticated) => {
                if (isAuthenticated) {
                    if (aboveThreshold) {
                        return this.paymentMethodSelectedAboveThreshold.asObservable().pipe(distinctUntilChanged());
                    }
                    return this.paymentMethodSelected.asObservable().pipe(distinctUntilChanged());
                }
                return this.getPaymentMethodGuest().pipe(
                    map((paymentMethodGuest: PaymentMethodGuest | null) => {
                        if (!paymentMethodGuest?.paymentMethod) {
                            return null;
                        }
                        return {
                            method: paymentMethodGuest.paymentMethod,
                        } as PaymentMethodSelected;
                    })
                );
            })
        );
    }

    /**
     * @method instant_guest_payment_information()
     * @description Return current guestPaymentInformation value
     */

    get instant_guest_payment_information(): PaymentMethodGuest | null {
        return this.guestPaymentInformation.value;
    }

    /**
     * @method guestCustomerInformation()
     * @description Return guestCustomerInformation value
     */

    getGuestPaymentInformation$(): Observable<PaymentMethodGuest | null> {
        return this.guestPaymentInformation.asObservable();
    }

    /**
     * @method setGuestPaymentInformation
     * @param paymentMethodGuest - The guest payment method to be set.
     * @description Sets the guest payment method.
     */
    setGuestPaymentInformation(paymentMethodGuest: PaymentMethodGuest | null): void {
        this.guestPaymentInformation.next(paymentMethodGuest);
    }

    getPaymentMethodsSelectedName$(): Observable<PaymentMethodsSelected> {
        return this.getPaymentMethodSelected$(true).pipe(
            switchMap((paymentMethodSelectedAboveThreshold: PaymentMethodSelected | null) => {
                return this.getPaymentMethodSelected$(false).pipe(
                    map((paymentMethodSelected: PaymentMethodSelected | null) => {
                        return {
                            paymentMethodSelected: paymentMethodSelected?.method as PaymentMethodsType,
                            paymentMethodSelectedAboveThreshold:
                                paymentMethodSelectedAboveThreshold?.method as PaymentMethodsType,
                        };
                    })
                );
            })
        );
    }

    getPaymentMethodsSelectedBasedOnThreshold$(
        isCartAboveThreshold: boolean,
        isCartBelowThreshold: boolean
    ): Observable<PaymentMethodsSelected> {
        if (isCartAboveThreshold && isCartBelowThreshold) {
            return this.getPaymentMethodsSelectedName$();
        }
        if (isCartAboveThreshold && !isCartBelowThreshold) {
            return this.getPaymentMethodSelected$(true).pipe(
                map((paymentMethodSelectedAboveThreshold: PaymentMethodSelected | null) => {
                    return {
                        paymentMethodSelected: undefined,
                        paymentMethodSelectedAboveThreshold:
                            paymentMethodSelectedAboveThreshold?.method as PaymentMethodsType,
                    };
                })
            );
        }
        if (this.sessionService.isAuthenticated()) {
            return this.getPaymentMethodSelected$(false).pipe(
                map((paymentMethodSelected: PaymentMethodSelected | null) => {
                    return {
                        paymentMethodSelected: paymentMethodSelected?.method as PaymentMethodsType,
                        paymentMethodSelectedAboveThreshold: undefined,
                    };
                })
            );
        }
        return this.paymentMethodGuest.asObservable().pipe(
            map((paymentMethodGuest: PaymentMethodGuest | null) => {
                return {
                    paymentMethodSelected: paymentMethodGuest?.paymentMethod as PaymentMethodsType,
                    paymentMethodSelectedAboveThreshold: undefined,
                };
            })
        );
    }

    isSettingDefaultPaymentMethod$(): Observable<boolean> {
        return this.settingDefaultPaymentMethod.asObservable();
    }

    setSettingDefaultPaymentMethod(value: boolean): void {
        this.settingDefaultPaymentMethod.next(value);
    }

    /**
     * @method getEventsPaymentMethods()
     * @description Listens for payment method events and updates the payment method accordingly.
     *              This method subscribes to events when a new payment method (Credit Card or Echeck) is added,
     *              and sets it as the default payment method if specified.
     * @returns {Observable<string>} An observable that completes with the string 'Complete'.
     */
    getEventsPaymentMethods(): Observable<string> {
        return this.paymentMethodsService.onEventPaymentMethod$().pipe(
            mergeMap((paymentMethodAction: PaymentMethodsEvents | null) => {
                if (
                    paymentMethodAction === PaymentMethodsEvents.creditCardAdded ||
                    paymentMethodAction === PaymentMethodsEvents.creditCardRemoved ||
                    paymentMethodAction === PaymentMethodsEvents.echeckAdded ||
                    paymentMethodAction === PaymentMethodsEvents.echeckRemoved
                ) {
                    return this.updatePaymentMethods().pipe(
                        switchMap(() => {
                            if (paymentMethodAction != null) {
                                return this.getLastPaymentMethod(paymentMethodAction).pipe(
                                    map((lastPaymentMethod: EventPaymentMethod) => {
                                        if (lastPaymentMethod?.item && lastPaymentMethod?.method) {
                                            const isCartAboveThreshold =
                                                this.paymentMethodsService.instant_event_above_threshold;

                                            if (!isCartAboveThreshold) {
                                                this.setPaymentMethodSelected({
                                                    paymentAccount: lastPaymentMethod.item,
                                                    method: lastPaymentMethod.method,
                                                    token: lastPaymentMethod.item.paymentToken?.token || undefined,
                                                });
                                            } else {
                                                this.setPaymentMethodSelectedAboveThreshold({
                                                    paymentAccount: lastPaymentMethod.item,
                                                    method: lastPaymentMethod.method,
                                                    token: lastPaymentMethod.item.paymentToken?.token || undefined,
                                                });
                                            }
                                        }

                                        this.paymentMethodsService.newEventPaymentMethod(PaymentMethodsEvents.unknown);
                                        return 'Complete';
                                    })
                                );
                            }

                            return of('Complete');
                        })
                    );
                }

                return of('Complete');
            })
        );
    }

    private updatePaymentMethods(): Observable<string> {
        return this.paymentMethodsService.getPaymentMethodsRequest$().pipe(
            take(1),
            map(() => 'Complete')
        );
    }

    private getLastPaymentMethod(event: PaymentMethodsEvents): Observable<EventPaymentMethod> {
        if (event === PaymentMethodsEvents.creditCardAdded) {
            return this.paymentMethodsService.getPaymentMethodsByType$(PaymentMethods.CREDIT_CARD).pipe(
                take(1),
                map((paymentMethod: PaymentMethod | undefined) => {
                    const cards = paymentMethod?.items || [];
                    const method: PaymentMethodsType = PaymentMethods.CREDIT_CARD;
                    const item: Card = cards[0];
                    return { item, method };
                })
            );
        }
        if (event === PaymentMethodsEvents.echeckAdded) {
            return this.paymentMethodsService.getPaymentMethodsByType$(PaymentMethods.ECHECK).pipe(
                take(1),
                map((paymentMethod: PaymentMethod | undefined) => {
                    const echeks = paymentMethod?.items || [];
                    const method: PaymentMethodsType = PaymentMethods.ECHECK;
                    const lengthEcheck = echeks.length;
                    const item: Echeck = echeks[lengthEcheck - 1];
                    return { item, method };
                })
            );
        }

        return of({ item: undefined, method: null });
    }

    /*
     * If there is no selected payment method, get the available payment methods. If there is only one, select it.
     * If there are two payment methods and there are invoices in the cart, select the one that is not CargoSprint Credit.
     * If there is a frequent payment method, select it.
     * If there is not enough credit or there are no credit cards or eChecks, select the default payment method in paymentMethodForNewPayments/paymentMethodForOpenInvoices
     */
    getPaymentMethodByDefault(): Observable<PaymentMethodSelected | null> {
        return this.paymentMethodsService.getPaymentMethods$().pipe(
            take(1),
            map((paymentMethods: PaymentMethod[]) => {
                return paymentMethods.filter(
                    (method) => method.defaultPaymentMethod || method.defaultPaymentMethod === undefined
                );
            }),
            switchMap((paymentMethods: PaymentMethod[]) => {
                if (paymentMethods.length === 1) {
                    return this.paymentMethodsService.getPaymentMethodValidated(paymentMethods[0].name, paymentMethods);
                }
                if (
                    paymentMethods.length === 2 &&
                    !!paymentMethods.find((paymentMethod) =>
                        PaymentMethod.isCreditMethod(paymentMethod.name as CreditMethodsType)
                    )
                ) {
                    return this.paymentMethodsService.getPaymentMethodValidated(
                        paymentMethods.filter(
                            (paymentMethod: PaymentMethod) =>
                                !PaymentMethod.isCreditMethod(paymentMethod.name as CreditMethodsType)
                        )[0]?.name,
                        paymentMethods
                    );
                }
                return of(null);
            })
        );
    }

    getPaymentTokenOfPaymentMethodSelected$(
        aboveThreshold?: boolean,
        isGuest?: boolean,
        isThresholdAndPaymentMethodsUnAvailable?: boolean
    ): Observable<PaymentToken | null> {
        return this.getPaymentMethodSelected$(aboveThreshold).pipe(
            switchMap((method: PaymentMethodSelected | null) => {
                if (!method) {
                    return of(null);
                }

                if (method && !isGuest) {
                    return this.paymentMethodsService.getPaymentToken(method.method, method?.token);
                }

                if (isThresholdAndPaymentMethodsUnAvailable) {
                    return of(
                        PaymentMethod.fromJson({
                            id: 0,
                            name: PaymentMethods.CREDIT_CARD,
                            paymentToken: {
                                token: null,
                                paymentMethod: null,
                                paymentProcessor: null,
                            },
                        })
                    ).pipe(
                        switchMap((paymentMethod: PaymentMethod | undefined) => {
                            if (!paymentMethod) {
                                return of(null);
                            }

                            return this.paymentMethodsService.getPaymentToken(method.method);
                        })
                    );
                }

                return this.paymentMethodsService.getPaymentMethodsByType$(method.method).pipe(
                    switchMap((paymentMethod: PaymentMethod | undefined) => {
                        if (!paymentMethod) {
                            return of(null);
                        }

                        return this.paymentMethodsService.getPaymentToken(method.method);
                    })
                );
            })
        );
    }
}
